import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CheckIcon } from "@heroicons/react/24/outline";
import { api, useAxiosInterceptor } from "../shared/axiosInstance";

const Assessment = ({ userId: propUserId }) => {
    useAxiosInterceptor();
    const navigate = useNavigate();
    const location = useLocation();

    // Use provided userId or fallback to localStorage
    const [userId] = useState(propUserId || localStorage.getItem("userId"));
    const [questions, setQuestions] = useState([]);
    // Store responses keyed by question ID
    const [responses, setResponses] = useState({});
    const unansweredCount = questions.filter(q => !responses[q._id]).length;
    const [language, setLanguage] = useState("english");
    // Store user flags from the user record (allQuestionsAnswered, resultsViewed)
    const [userFlags, setUserFlags] = useState({
        allQuestionsAnswered: false,
        resultsViewed: false,
    });

    const [showInstructionsModal, setShowInstructionsModal] = useState(false);

    // Prevent duplicate API calls
    const hasFetchedQuestions = useRef(false);
    const hasFetchedResponses = useRef(false);

    // Detect language from URL
    useEffect(() => {
        setLanguage(location.pathname.includes("/pt") ? "portuguese" : "english");
    }, [location.pathname]);

    // Fetch active questions once
    useEffect(() => {
        if (!userId) {
            console.warn("❌ User ID is missing! Redirecting to Sign In...");
            navigate("/signin");
            return;
        }
        if (hasFetchedQuestions.current) return;
        hasFetchedQuestions.current = true;

        const fetchQuestions = async () => {
            try {
                const response = await api.get(`/response/active-questions/${language}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                });
                setQuestions(response.data.questions);

                // Shuffle unanswered questions
                const shuffleArray = (array) => array.sort(() => Math.random() - 0.5);

                const allQuestions = response.data.questions || [];
                setQuestions(shuffleArray(allQuestions)); // Shuffle once before setting state
            } catch (err) {
                console.error("❌ Error fetching questions:", err);
            }
        };

        fetchQuestions();
    }, [userId, language, navigate]);

    // Fetch user responses once and store them keyed by question ID
    useEffect(() => {
        if (!userId || hasFetchedResponses.current) return;
        hasFetchedResponses.current = true;

        const fetchUserResponses = async () => {
            try {
                const response = await api.get(`/response/user/${userId}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                });
                setResponses(
                    response.data.responses.reduce((acc, r) => {
                        acc[r.questionId] = r;
                        return acc;
                    }, {})
                );
            } catch (err) {
                console.error("❌ Error fetching user responses:", err);
            }
        };

        fetchUserResponses();
    }, [userId]);

    useEffect(() => {
        if (!userId) return;
        const fetchUserFlags = async () => {
            try {
                const response = await api.get(`/auth/user/${userId}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                });
                const userData = response.data;
                setUserFlags({
                    allQuestionsAnswered: userData.allQuestionsAnswered,
                    resultsViewed: userData.resultsViewed,
                });

                if (unansweredCount === questions.length) {
                    setShowInstructionsModal(true);
                } else {
                    setShowInstructionsModal(false);
                }

            } catch (err) {
                console.error("❌ Error fetching user flags:", err);
            }
        };

        fetchUserFlags();
    }, [userId, questions.length, unansweredCount]);

    const activeQuestionIds = new Set(questions.map((q) => q._id));
    const answeredActiveQuestions = Object.keys(responses).filter((qId) =>
        activeQuestionIds.has(qId)
    ).length;

    const showWelcomeBanner = questions.length > 0 && answeredActiveQuestions === 0 && !userFlags.allQuestionsAnswered;
    const showCompletionMessage = userFlags.allQuestionsAnswered;
    const journeyStepIndex = userFlags.allQuestionsAnswered ? 1 : 0;

    // Split questions into unanswered (random order) and answered (sorted by updatedAt descending)
    const unansweredQuestions = questions
        .filter((q) => !responses[q._id]);
    const answeredQuestions = questions
        .filter((q) => responses[q._id])
        .sort((a, b) => new Date(responses[b._id]?.updatedAt) - new Date(responses[a._id]?.updatedAt));
    // Handle response change; block changes if results have been viewed
    const handleResponseChange = async (questionId, selectedValue) => {
        if (!userId) return navigate("/signin");
        if (userFlags.resultsViewed) return; // editing is blocked if results have been viewed

        // Optimistically update local state with current timestamp
        const updatedResponse = {
            response: selectedValue,
            updatedAt: new Date().toISOString(),
        };
        setResponses((prevResponses) => ({
            ...prevResponses,
            [questionId]: updatedResponse,
        }));

        try {
            await api.post(
                `/response/submit`,
                {
                    userId,
                    questionId,
                    response: selectedValue,
                },
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                }
            );

            // Create a temporary object that represents the new state
            const currentResponses = { ...responses, [questionId]: updatedResponse };

            const unansweredCount = questions.filter(q => !currentResponses[q._id]).length;
            if (unansweredCount === 0) {
                await api.put(
                    `/auth/user/${userId}`,
                    { allQuestionsAnswered: true, assessmentCompletedAt: new Date().toISOString() },
                    { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
                );
                // Update local state to reflect the change
                setUserFlags((prevFlags) => ({ ...prevFlags, allQuestionsAnswered: true }));

                // ✅ NEW: Notify the backend to check if the user is a target and update their status
                try {
                    await api.put(`/jobs/check-target/${userId}`, {
                        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
                    });
                } catch (error) {
                    console.error("Error updating target status:", error);
                }


                // Trigger the analysis process
                try {
                    await api.post('/run-analysis');
                } catch (error) {
                    console.error("Error triggering analysis:", error);
                }
            }

        } catch (err) {
            console.error("❌ Error submitting response:", err);
        }
    };

    // Handler for retaking the survey
    const handleRetake = async () => {
        const confirmRetake = window.confirm(
            `Are you sure you want to retake the survey? This will clear your responses for ${questions.length} active questions.`
        );
        if (confirmRetake) {
            try {
                await api.post(
                    "/response/retake",
                    {},
                    { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
                );
                // Clear local responses and reset flags
                setResponses({});
                setUserFlags({ allQuestionsAnswered: false, resultsViewed: false });
                alert("Survey has been reset. You may now retake the assessment.");
            } catch (error) {
                console.error("❌ Error resetting survey:", error);
                alert("Failed to reset the survey. Please try again.");
            }
        }
    };

    const journeySteps = ["Complete Assessment", "Get Matched", "View Results"];

    const CandidateJourneyTracker = ({ currentStepIndex }) => (
        <div className="flex items-center justify-center gap-3 mb-6 w-full max-w-4xl">
            {journeySteps.map((label, idx) => (
                <div key={idx} className="flex items-center">
                    <div className="flex flex-col items-center">
                        <div
                            className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                                idx < currentStepIndex
                                    ? "bg-darkblue text-white"
                                    : idx === currentStepIndex
                                    ? "border-2 border-orange text-white"
                                    : "border border-lightblue text-lightgray"
                            }`}
                            aria-current={idx === currentStepIndex ? "step" : undefined}
                        >
                            {idx < currentStepIndex ? <CheckIcon className="w-4 h-4" /> : idx + 1}
                        </div>
                        <span className="text-xs text-lightgray mt-1">{label}</span>
                    </div>
                    {idx < journeySteps.length - 1 && (
                        <div className="w-8 h-px bg-lightblue mx-1" />
                    )}
                </div>
            ))}
        </div>
    );

    return (
        <div className="flex flex-col items-center bg-darkblue text-lightgray p-4 pt-12">
        {/* Instructions Modal */}
            {showInstructionsModal && unansweredCount === questions.length && (
                <div
                    className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50"
                    onClick={() => setShowInstructionsModal(false)} // ✅ Close when clicking outside
                >
                    <div
                        className="bg-white text-black p-6 rounded-lg shadow-lg w-11/12 max-w-lg relative"
                        onClick={(e) => e.stopPropagation()} // ✅ Prevent closing when clicking inside
                    >
                        {/* Close Button */}
                        <button
                            className="absolute top-3 right-3 text-gray-600 hover:text-gray-900"
                            onClick={() => setShowInstructionsModal(false)}
                        >
                            ✕
                        </button>

                        <h2 className="text-xl font-bold text-orange mb-4 text-center">Instructions</h2>
                        <p className="text-sm mb-4">
                            The CareerMission Personality Inventory captures your career personality as a
                            collection of empirically-validated numbers. The CareerMission algorithm uses these
                            numbers to match you to people who have similar career personalities to yours. If
                            your scores are similar to another person's, you are similar to them
                            in terms of the kind of work you will enjoy, and the kinds of people you'll enjoy
                            working with.
                        </p>
                        <p className="text-sm mb-4">
                            The assessment contains 120 items measuring career identity,
                            and 50 items measuring the Big 5 Personality Factors. Average time to complete is 20 minutes.
                        </p>
                        <p className="text-sm mb-4">
                            Your answers on the questions and your responses are 100% confidential: YOU
                            are the only person who will see them. It's extremely important that you answer every
                            question honestly. Answering in a way that you believe will impress employers will
                            distort your results, and decrease your likelihood of getting matches.
                        </p>
                        <p className="text-sm mb-4">
                            For each statement, select the answer that describes you most of the time. Don't spend
                            too much time thinking about any one question—rely on your first impression. When you
                            have completed the assessment, go to the Matches tab to see your matches!
                        </p>

                            <button
                                onClick={() => setShowInstructionsModal(false)}
                                className="px-4 py-2 bg-orange text-white rounded hover:bg-deeporange"
                            >
                                Proceed to Assessment
                            </button>
                    </div>
                </div>
            )}

            {/* Assessment Content */}
            <h1 className="text-3xl font-bold text-orange mb-6">
                {language === "english" ? "Career Mission Personality Inventory" : "Pesquisa da Career Mission"}
            </h1>

            {/* Journey Tracker */}
            <CandidateJourneyTracker currentStepIndex={journeyStepIndex} />

            {/* Welcome Banner for new candidates */}
            {showWelcomeBanner && (
                <div className="bg-bluegray p-6 rounded-lg shadow-md mb-6 w-full max-w-4xl">
                    <h2 className="text-xl font-bold text-orange mb-4">Welcome to CareerMission</h2>
                    <div className="space-y-4 text-sm text-lightgray">
                        <div>
                            <h3 className="font-semibold text-lightblue mb-1">What the Assessment Measures</h3>
                            <p>This assessment captures your unique career personality through 170 research-backed questions covering career identity and the Big 5 personality factors. Your honest answers help us understand who you really are at work.</p>
                        </div>
                        <div>
                            <h3 className="font-semibold text-lightblue mb-1">How Matching Works</h3>
                            <p>Your results are compared against job requirements and team profiles to find roles where you will thrive. <Link to="/how-matching-works" className="text-lightblue underline hover:text-orange">Learn more about matching</Link></p>
                        </div>
                        <div>
                            <h3 className="font-semibold text-lightblue mb-1">What Happens After Completion</h3>
                            <p>Once you finish, your profile is automatically matched against open roles. You will see your matches on the Matches tab. <Link to="/faq" className="text-lightblue underline hover:text-orange">Read the FAQ</Link></p>
                        </div>
                    </div>
                </div>
            )}

            {/* Completion Message */}
            {showCompletionMessage && (
                <div className="bg-bluegray p-6 rounded-lg shadow-md mb-6 w-full max-w-4xl">
                    <p className="text-lightgray">
                        Your results are being processed. You will be matched against open roles automatically.{' '}
                        <Link to="/matches" className="text-lightblue underline hover:text-orange">View your matches</Link>
                    </p>
                </div>
            )}

            {/* Repeat Instructions at the Top of the Page */}
            <div className="bg-bluegray p-4 rounded-lg shadow-md mb-6 w-full max-w-4xl">

            <p className="text-sm">
                    The CareerMission Personality Inventory captures your career personality as a
                    collection of empirically-validated numbers. The CareerMission algorithm uses these
                    numbers to match you to people who have similar career personalities to yours.
                </p>
                <p className="text-sm mt-3">
                    The Assessment contains 120 items measuring career identity,
                    and 50 items measuring the Big 5 Personality Factors. Average time to complete is 20 minutes.
                </p>
                <p className="text-sm mt-3">
                    Your answers on the questions and your responses are 100% confidential: YOU
                    are the only person who will see them. It's extremely important that you answer every
                    question honestly.
                </p>
            </div>
            <div className="flex flex-col md:flex-row w-full max-w-4xl space-y-6 md:space-y-0 md:space-x-4">
                {/* Left Column: Unanswered Questions & Progress */}
                <div className="w-full md:w-1/2 p-2">
                    <div className="bg-bluegray shadow-md p-4 rounded-lg mb-6">
                        <p className="text-lg font-semibold text-lightgray">
                            {userFlags.allQuestionsAnswered
                                ? language === "english"
                                    ? "You have completed all questions! You can retake the survey if you feel your answers aren't accurate anymore."
                                    : "Você completou todas as perguntas! Você pode atualizar suas respostas a qualquer momento."
                                : language === "english"
                                    ? "For each statement below, select the answer that best describes you most of the time. Don't spend too much time thinking about any one question, rely on your first impression. Your responses are saved automatically."
                                    : "Para cada afirmação abaixo, selecione a resposta que melhor descreve como você se sente na maior parte do tempo. Suas respostas são salvas automaticamente."}
                        </p>
                        {userFlags.allQuestionsAnswered && (
                            <button
                                onClick={() => navigate("/results")}
                                className="w-full py-2 px-4 bg-orange hover:bg-deeporange text-white font-medium rounded transition duration-300 mt-4"
                            >
                                {language === "english" ? "View Results" : "Ver Resultados"}
                            </button>
                        )}{/* Retake Survey Button */}
                        {userFlags.allQuestionsAnswered && userFlags.resultsViewed && (
                            <div className="mt-6">
                                <button
                                    onClick={handleRetake}
                                    className="py-2 px-4 bg-red hover:bg-deeporange text-white font-medium rounded transition duration-300"
                                >
                                    {language === "english" ? "Retake Survey" : "Refazer Pesquisa"}
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="mb-6">
                        <p className="text-center text-lightgray font-semibold">
                            {answeredActiveQuestions} / {questions.length} questions answered
                        </p>
                        <div className="w-full bg-gray-700 rounded-full h-4 mt-2">
                            <div
                                className="bg-orange h-4 rounded-full transition-all duration-300"
                                style={{ width: `${(answeredActiveQuestions / questions.length) * 100}%` }}
                            />
                        </div>
                    </div>
                    <div className="space-y-6">
                        {unansweredQuestions.map((question) => (
                            <div key={question._id} className="bg-bluegray shadow-md p-4 rounded-lg">
                                <p className="text-lg font-semibold mb-3">{question.text}</p>
                                <div className="flex flex-col space-y-2">
                                    {question.choices
                                        .sort((a, b) => a.text.localeCompare(b.text))
                                        .map((choice) => (
                                            <label
                                                key={choice.score}
                                                className="flex items-center space-x-2 cursor-pointer"
                                            >
                                                <input
                                                    type="radio"
                                                    name={`question-${question._id}`}
                                                    value={choice.score}
                                                    checked={responses[question._id]?.response === choice.score}
                                                    onChange={() =>
                                                        handleResponseChange(question._id, choice.score)
                                                    }
                                                    disabled={userFlags.resultsViewed}
                                                    className="form-radio text-orange focus:ring-orange"
                                                />
                                                <span>{choice.text}</span>
                                            </label>
                                        ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                {/* Right Column: Answered Questions & Progress Bar */}
                <div className="w-full md:w-1/2 p-2">

                    <div className="space-y-6">
                        {answeredQuestions.map((question) => (
                            <div key={question._id} className="bg-bluegray shadow-md p-4 rounded-lg">
                                <p className="text-lg font-semibold mb-3">{question.text}</p>
                                <div className="flex flex-col space-y-2">
                                    {question.choices
                                        .sort((a, b) => a.text.localeCompare(b.text))
                                        .map((choice) => (
                                            <label
                                                key={choice.score}
                                                className="flex items-center space-x-2 cursor-pointer"
                                            >
                                                <input
                                                    type="radio"
                                                    name={`question-${question._id}`}
                                                    value={choice.score}
                                                    checked={responses[question._id]?.response === choice.score}
                                                    onChange={() =>
                                                        handleResponseChange(question._id, choice.score)
                                                    }
                                                    disabled={userFlags.resultsViewed}
                                                    className="form-radio text-orange focus:ring-orange"
                                                />
                                                <span>{choice.text}</span>
                                            </label>
                                        ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

        </div>
    );
};

export default Assessment;
